import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AtendimentoResumo,
  buscarFrotaPerfil,
  FatiaComPlacas,
  FiltrosOficina,
  RespostaFrotaPerfil,
} from '../../../api/dashboardOficinaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import {
  corDaSerie,
  corOrdinal,
  formatarMoeda,
  formatarMoedaCompacta,
  formatarNumero,
  TINTA_GRAFICO,
} from '../../../shared/ui/chart-palette';
import DetalhePlacasModal from '../../../shared/ui/DetalhePlacasModal';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';
import TabelaPaginada, { ColunaTabela } from '../../../shared/ui/TabelaPaginada';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';

export const helpSectionsFrotaPerfil: ComoFuncionaSecao[] = [
  {
    titulo: 'De onde vem esta aba',
    conteudo: (
      <>
        <p>
          Os veículos são os <strong>atendidos pela oficina</strong>; as características de cada um
          — categoria, marca, ano, valor protegido, consultor — vêm do{' '}
          <strong>cadastro do SGA</strong>, casado pela placa.
        </p>
        <p>
          O cadastro chega pela sincronização periódica, não por consulta ao SGA na hora. O que você
          vê reflete a última sincronização.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que nem todo veículo aparece nos gráficos',
    conteudo: (
      <>
        <p>
          Os gráficos desta aba são feitos <strong>apenas sobre os veículos localizados no
          cadastro do SGA</strong>, e a tela diz quantos são logo abaixo do título.
        </p>
        <p>
          A maior parte dos não localizados é de <strong>terceiros</strong>, e isso é esperado: o
          cadastro do SGA é dos associados. Veículo de terceiro entra na oficina, gera trabalho e
          aparece nas outras abas — só não tem ficha cadastral para enriquecer.
        </p>
        <p>
          Se um veículo de <strong>associado</strong> aparecer como não localizado, aí sim vale
          conferir a placa no SGA.
        </p>
      </>
    ),
  },
  {
    titulo: 'Valor protegido no pátio',
    conteudo: (
      <p>
        Soma do valor protegido dos veículos que estão na oficina <strong>agora</strong>. É o
        patrimônio sob responsabilidade da oficina neste momento — uma leitura de risco que a
        contagem de veículos não dá.
      </p>
    ),
  },
  {
    titulo: 'Custo de oficina por segmento',
    conteudo: (
      <>
        <p>
          Quantas <strong>horas de equipe</strong> um veículo de cada categoria e de cada faixa de
          idade consome, em média. Responde que tipo de veículo dá mais trabalho.
        </p>
        <p>
          A média é por veículo atendido, não por atendimento: veículo que voltou soma as horas das
          duas passagens.
        </p>
      </>
    ),
  },
  {
    titulo: 'Atendimentos por consultor',
    conteudo: (
      <>
        <p>
          Quantos veículos da carteira de cada consultor passaram pela oficina, e o tamanho da
          carteira dele ao lado — é ela que dá a escala.
        </p>
        <p>
          Não é medida de desempenho: a carteira do consultor não escolhe bater o carro. É leitura
          de risco.
        </p>
      </>
    ),
  },
  {
    titulo: 'Clique em qualquer barra para ver as placas',
    conteudo: (
      <p>
        Toda fatia dos gráficos e toda linha das tabelas abre a lista dos atendimentos que formam
        aquele número, com placa, modelo e esteira atual.
      </p>
    ),
  },
];

interface Props {
  filtros: FiltrosOficina;
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const FrotaPerfilTab: React.FC<Props> = ({ filtros, onJanela, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaFrotaPerfil | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<{
    titulo: string;
    placas: string[];
    colunas: Array<ColunaTabela<AtendimentoResumo>>;
  } | null>(null);

  const carregar = useCallback(
    async (f: FiltrosOficina) => {
      setCarregando(true);
      try {
        const resposta = await buscarFrotaPerfil(f);
        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.kpisRecorte === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.kpisRecorte === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        setDados(resposta);
        setErro(null);
        onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
      } catch (e) {
        setDados(null);
        setErro(
          'Não foi possível carregar o perfil da frota. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
        );
        notify.errorFrom(
          e,
          'Não foi possível carregar o perfil da frota. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'dashboard-oficina-frota-perfil-falhou' },
        );
      } finally {
        setCarregando(false);
      }
    },
    [onJanela],
  );

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const primeiraCarga = carregando && !respondeu;
  const textoVazio = respondeu
    ? 'Nenhum veículo deste recorte foi localizado no cadastro do SGA.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const cobertura = dados?.cobertura;
  const abrir = (
    titulo: string,
    placas: string[],
    colunas: Array<ColunaTabela<AtendimentoResumo>>,
  ) => setDetalhe({ titulo, placas, colunas });

  /**
   * Colunas dos modais desta aba.
   *
   * ⚠️ Cada modal mostra a informação do PRÓPRIO recorte: quem abre "por categoria" vê a
   * categoria na linha, quem abre um valor vê o valor de cada veículo. Colunas genéricas
   * (classe, esteira, dias) não explicam o número que o modal abriu.
   */
  const COL = {
    categoria: { titulo: 'Categoria', largura: 200, texto: (i: AtendimentoResumo) => i.categoria ?? '—' },
    marca: { titulo: 'Marca', largura: 160, texto: (i: AtendimentoResumo) => i.marca ?? '—' },
    ano: { titulo: 'Ano-modelo', largura: 120, texto: (i: AtendimentoResumo) => (i.anoModelo ? String(i.anoModelo) : '—') },
    idade: { titulo: 'Idade', largura: 110, texto: (i: AtendimentoResumo) => (i.idadeAnos != null ? i.idadeAnos + ' anos' : '—') },
    valor: { titulo: 'Valor protegido', largura: 160, texto: (i: AtendimentoResumo) => (i.valorProtegido != null ? formatarMoeda(i.valorProtegido) : '—') },
    consultor: { titulo: 'Consultor', largura: 240, texto: (i: AtendimentoResumo) => i.consultor ?? '—' },
    cooperativa: { titulo: 'Cooperativa', largura: 240, texto: (i: AtendimentoResumo) => i.cooperativa ?? '—' },
    faixaEtaria: { titulo: 'Faixa etária', largura: 160, texto: (i: AtendimentoResumo) => i.faixaEtaria ?? '—' },
    tempoAssoc: { titulo: 'Tempo de associação', largura: 190, texto: (i: AtendimentoResumo) => i.tempoDeAssociacao ?? '—' },
    horas: { titulo: 'Horas de equipe', largura: 150, texto: (i: AtendimentoResumo) => `${i.horasTrabalhadas} h` },
    situacaoAssociado: { titulo: 'Situação do associado', largura: 185, texto: (i: AtendimentoResumo) => i.situacaoAssociado ?? '—' },
  };

  /** Só vira botão quando há placa por trás — indicador zerado não abre modal vazio. */
  const detalharSe = (
    titulo: string,
    placas: string[] | undefined,
    colunas: Array<ColunaTabela<AtendimentoResumo>>,
  ) => (placas && placas.length > 0 ? () => abrir(titulo, placas, colunas) : undefined);

  /** Barra horizontal clicável, que é a forma da maioria dos gráficos desta aba. */
  const barrasDe = (
    fatias: FatiaComPlacas[],
    titulo: string,
    cor: string,
    colunasDoGrafico: Array<ColunaTabela<AtendimentoResumo>>,
  ) => (
    <div className="do-plot">
      {!respondeu || fatias.length === 0 ? (
        <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={fatias} layout="vertical" margin={{ left: 8, right: 16 }}>
            <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
            <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
            <YAxis type="category" dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={150} />
            <Tooltip
              contentStyle={ESTILO_TOOLTIP}
              formatter={(valor: number) => [formatarNumero(valor), 'Veículos']}
            />
            <Bar
              dataKey="quantidade"
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              onClick={(evento: any) =>
                evento?.payload &&
                abrir(`${titulo}: ${evento.payload.rotulo}`, evento.payload.placas, colunasDoGrafico)
              }
            >
              {fatias.map((fatia) => (
                <Cell key={fatia.rotulo} fill={cor} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Frota &amp; Perfil
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
      </div>

      {/*
        ⚠️ A cobertura é declarada ANTES dos gráficos, não num rodapé: sem ela, uma pizza feita
        sobre metade dos veículos passa por retrato do todo.
      */}
      {respondeu && cobertura && (
        <p className="do-nota-janela">
          Os gráficos abaixo descrevem os{' '}
          <strong>
            {formatarNumero(cobertura.localizados)} de {formatarNumero(cobertura.atendimentos)}
          </strong>{' '}
          veículos deste recorte que foram localizados no cadastro do SGA.
          {cobertura.naoLocalizados > 0 && (
            <>
              {' '}
              Os outros {formatarNumero(cobertura.naoLocalizados)} não têm ficha cadastral —{' '}
              {formatarNumero(cobertura.naoLocalizadosTerceiros)}{' '}
              {cobertura.naoLocalizadosTerceiros === 1 ? 'é de terceiro' : 'são de terceiros'}, o que
              é esperado, porque o cadastro do SGA é dos associados.
            </>
          )}
        </p>
      )}

      <p className="do-nota-janela">
        <strong>No recorte selecionado</strong> — estes quatro respeitam o filtro aplicado na tela.
      </p>
      <div className="do-kpis">
        <Kpi
          rotulo="Veículos no recorte"
          valor={respondeu ? formatarNumero(dados.kpisRecorte.veiculos) : '-'}
          apoio="Atendimentos do filtro atual"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Veículos no recorte', dados?.kpisRecorte.placas, [COL.categoria, COL.marca, COL.valor])}
          detalheRotulo="Ver os veículos do recorte"
        />
        <Kpi
          rotulo="Valor protegido"
          valor={respondeu ? formatarMoedaCompacta(dados.kpisRecorte.valorProtegido) : '-'}
          apoio="Soma dos veículos do recorte com cadastro"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Valor protegido no recorte', dados?.kpisRecorte.placas, [COL.valor, COL.categoria])}
          detalheRotulo="Ver os veículos somados neste valor"
        />
        <Kpi
          rotulo="Valor médio por veículo"
          valor={respondeu ? formatarMoeda(dados.kpisRecorte.valorMedioPorVeiculo) : '-'}
          apoio="Média entre os veículos do recorte"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Valor médio por veículo (recorte)', dados?.kpisRecorte.placas, [COL.valor, COL.categoria])}
          detalheRotulo="Ver os veículos que entram nesta média"
        />
        <Kpi
          rotulo="Idade média"
          valor={respondeu ? dados.kpisRecorte.idadeMediaAnos + ' anos' : '-'}
          apoio="Pelo ano-modelo do cadastro"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Idade média (recorte)', dados?.kpisRecorte.placas, [COL.ano, COL.idade, COL.categoria])}
          detalheRotulo="Ver os veículos que entram nesta média"
        />
      </div>

      {/*
        ⚠️ O segundo bloco IGNORA o filtro de propósito: responde "o que está no pátio agora".
        Sem essa separação, "valor médio entre os localizados" não dizia se respeitava o recorte.
      */}
      <p className="do-nota-janela">
        <strong>Na oficina agora</strong> — o que está no pátio neste momento,{' '}
        <strong>independentemente do filtro</strong> acima.
      </p>
      <div className="do-kpis">
        <Kpi
          rotulo="Veículos no pátio"
          valor={respondeu ? formatarNumero(dados.kpisNaOficina.veiculos) : '-'}
          apoio="Em atendimento neste momento"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Veículos na oficina agora', dados?.kpisNaOficina.placas, [COL.categoria, COL.marca, COL.valor])}
          detalheRotulo="Ver os veículos que estão na oficina"
        />
        <Kpi
          rotulo="Valor protegido no pátio"
          valor={respondeu ? formatarMoedaCompacta(dados.kpisNaOficina.valorProtegido) : '-'}
          apoio="Patrimônio sob responsabilidade da oficina"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Valor protegido no pátio', dados?.kpisNaOficina.placas, [COL.valor, COL.categoria])}
          detalheRotulo="Ver os veículos somados neste valor"
        />
        <Kpi
          rotulo="Valor médio no pátio"
          valor={respondeu ? formatarMoeda(dados.kpisNaOficina.valorMedioPorVeiculo) : '-'}
          apoio="Média entre os que estão na oficina"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Valor médio no pátio', dados?.kpisNaOficina.placas, [COL.valor, COL.categoria])}
          detalheRotulo="Ver os veículos que entram nesta média"
        />
        <Kpi
          rotulo="Idade média no pátio"
          valor={respondeu ? dados.kpisNaOficina.idadeMediaAnos + ' anos' : '-'}
          apoio="Pelo ano-modelo do cadastro"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Idade média no pátio', dados?.kpisNaOficina.placas, [COL.ano, COL.idade, COL.categoria])}
          detalheRotulo="Ver os veículos que entram nesta média"
        />
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Veículos por categoria"
          descricao="Categoria declarada no cadastro do SGA. Clique numa barra para ver as placas."
        >
          {barrasDe(dados?.porCategoria ?? [], 'Categoria', corDaSerie(0), [COL.categoria, COL.marca])}
        </CardGrafico>

        <CardGrafico
          titulo="Veículos por marca"
          descricao="As marcas que mais aparecem na oficina. Clique para ver as placas."
        >
          {barrasDe(dados?.porMarca ?? [], 'Marca', corDaSerie(2), [COL.marca, COL.categoria])}
        </CardGrafico>

        <CardGrafico
          titulo="Idade da frota atendida"
          descricao="Faixas pelo ano-modelo. A cor escurece conforme a faixa avança."
        >
          <div className="do-plot">
            {!respondeu || dados.idadeFrota.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={dados.idadeFrota}
                    dataKey="quantidade"
                    nameKey="rotulo"
                    innerRadius={55}
                    outerRadius={95}
                    cursor="pointer"
                    onClick={(evento: any) =>
                      evento?.payload?.payload &&
                      abrir(`Idade: ${evento.payload.payload.rotulo}`, evento.payload.payload.placas, [COL.ano, COL.idade, COL.categoria])
                    }
                  >
                    {dados.idadeFrota.map((fatia, indice) => (
                      <Cell key={fatia.rotulo} fill={corOrdinal(indice, dados.idadeFrota.length)} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(v: number) => [formatarNumero(v), 'Veículos']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <Legenda
            itens={(dados?.idadeFrota ?? []).map((fatia, indice) => ({
              rotulo: fatia.rotulo,
              cor: corOrdinal(indice, dados?.idadeFrota.length ?? 1),
            }))}
          />
        </CardGrafico>

        <CardGrafico
          titulo="Faixa etária dos associados"
          descricao="Idade do dono do veículo atendido, pelo cadastro do SGA."
        >
          {barrasDe(dados?.faixaEtariaAssociados ?? [], 'Faixa etária', corDaSerie(4), [COL.faixaEtaria, COL.categoria])}
        </CardGrafico>

        <CardGrafico
          titulo="Tempo de associação"
          descricao="Há quanto tempo o dono é associado. Concentração nas faixas curtas merece atenção: pode indicar veículo que entrou na associação já precisando de reparo."
        >
          {barrasDe(dados?.tempoDeAssociacao ?? [], 'Tempo de associação', corDaSerie(6), [COL.tempoAssoc, COL.categoria])}
        </CardGrafico>

        <CardGrafico
          titulo="Por cooperativa"
          descricao="Cooperativa do veículo atendido, pelo cadastro."
        >
          {barrasDe(dados?.porCooperativa ?? [], 'Cooperativa', corDaSerie(8), [COL.cooperativa, COL.consultor])}
        </CardGrafico>
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Custo de oficina por categoria"
          descricao="Horas de equipe que um veículo de cada categoria consome, em média. Mostra que tipo de veículo dá mais trabalho. Clique numa barra para ver as placas."
        >
          <div className="do-plot">
            {!respondeu || dados.custoPorCategoria.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dados.custoPorCategoria} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis type="category" dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} width={150} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: number) => [`${valor} h`, 'Média por veículo']}
                  />
                  <Bar
                    dataKey="horasMedias"
                    fill={corDaSerie(1)}
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(evento: any) =>
                      evento?.payload &&
                      abrir(`Custo por categoria: ${evento.payload.rotulo}`, evento.payload.placas, [
                        COL.horas,
                        COL.categoria,
                      ])
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Custo de oficina por idade do veículo"
          descricao="Mesma leitura, por faixa de ano-modelo, da mais nova para a mais velha — a resposta está na inclinação. Clique numa barra para ver as placas."
        >
          <div className="do-plot">
            {!respondeu || dados.custoPorIdade.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dados.custoPorIdade} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="rotulo" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} formatter={(valor: number) => [`${valor} h`, 'Média por veículo']} />
                  <Bar
                    dataKey="horasMedias"
                    fill={corDaSerie(3)}
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(evento: any) =>
                      evento?.payload &&
                      abrir(`Custo por idade: ${evento.payload.rotulo}`, evento.payload.placas, [
                        COL.horas,
                        COL.ano,
                        COL.idade,
                      ])
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Uso da oficina por consultor"
          descricao="Atendimentos por consultor, com o tamanho da carteira dele no SGA ao lado — é ela que dá a escala. Clique num nome para ver os veículos."
        >
          {!respondeu || dados.porConsultor.length === 0 ? (
            <div className="do-plot">
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            </div>
          ) : (
            <TabelaPaginada
              itens={dados.porConsultor}
              chave={(linha) => linha.rotulo}
              vazio="Nenhum consultor neste recorte."
              onClickLinha={(linha) => abrir(`Consultor: ${linha.rotulo}`, linha.placas, [COL.consultor, COL.categoria, COL.valor])}
              colunas={[
                { titulo: 'Consultor', largura: 260, texto: (l) => l.rotulo },
                { titulo: 'Atendimentos', largura: 120, texto: (l) => formatarNumero(l.quantidade) },
                {
                  titulo: 'Carteira no SGA',
                  largura: 140,
                  texto: (l) => (l.carteira ? formatarNumero(l.carteira) : '—'),
                },
              ]}
            />
          )}
        </CardGrafico>
      </div>

      {detalhe && dados && (
        <DetalhePlacasModal
          titulo={detalhe.titulo}
          placas={detalhe.placas}
          colunas={detalhe.colunas}
          atendimentos={dados.atendimentos}
          onFechar={() => setDetalhe(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Frota e Perfil"
        secoes={helpSectionsFrotaPerfil}
      />
    </div>
  );
};

export default FrotaPerfilTab;
